import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ExternalLink, Loader2, Trash2, FileDown, X } from "lucide-react";

const fmtMoney = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
};

const fmtDate = (s) => {
  try { return new Date(s).toLocaleString(); } catch { return s || ""; }
};

const TEMPLATE_LABELS = {
  municipal_finance_template:  "Municipal Finance",
  vendor_procurement_template: "Vendor Procurement",
  infrastructure_template:     "Infrastructure",
  traffic_intersection_template: "Traffic / Intersection",
  invoice_template: "Invoice",
  grants_template: "Grants",
  payroll_template: "Payroll",
  water_template: "Water System",
  athlete_evaluation_template: "Athlete Evaluation",
  business_efficiency_template: "Business Efficiency",
};

const STATUS_CLASSES = {
  Complete: "text-[var(--auditiq-success,#5bbf85)]",
  Running:  "text-gold",
  Pending:  "text-cream-dim",
  Failed:   "text-[var(--auditiq-critical,#e85b5b)]",
};

const AuditHistory = () => {
  const [jobs, setJobs] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [perms, setPerms] = useState(null);
  const [error, setError] = useState("");

  // Filters
  const [industryFilter, setIndustryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Delete confirm modal
  const [confirmDelete, setConfirmDelete] = useState(null); // job row
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get("/auditiq/jobs")
      .then(r => setJobs(r.data?.jobs || []))
      .catch(e => setError(e?.response?.data?.detail || "Failed to load audit history"));
    api.get("/auditiq/industries")
      .then(r => setIndustries(r.data?.industries || []))
      .catch(() => {});
    api.get("/auditiq/my-permissions")
      .then(r => setPerms(r.data))
      .catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!jobs) return [];
    return jobs.filter(j => {
      if (industryFilter !== "all" && (j.industry || "municipality") !== industryFilter) return false;
      if (statusFilter !== "all" && j.status !== statusFilter) return false;
      if (dateFrom) {
        const t = new Date(j.created_at).getTime();
        if (isNaN(t) || t < new Date(dateFrom).getTime()) return false;
      }
      if (dateTo) {
        const t = new Date(j.created_at).getTime();
        if (isNaN(t) || t > new Date(dateTo + "T23:59:59").getTime()) return false;
      }
      return true;
    });
  }, [jobs, industryFilter, statusFilter, dateFrom, dateTo]);

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/auditiq/audit/${confirmDelete.audit_id}`);
      setJobs(js => js.filter(j => j.audit_id !== confirmDelete.audit_id));
      toast.success("Audit deleted.");
      setConfirmDelete(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
    setDeleting(false);
  };

  const exportPdf = async (audit_id) => {
    try {
      const r = await api.get(`/auditiq/report/${audit_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `AuditIQ_Report_${audit_id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    }
  };

  const canDelete = !!perms?.permissions?.can_delete_audits;

  return (
    <PortalLayout>
      <div className="space-y-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">AuditIQ</div>
          <h1 className="font-display text-3xl md:text-4xl mt-2">Audit History</h1>
          <p className="text-sm text-cream-dim mt-1">All past audit runs for your organization.</p>
        </div>

        {/* Filter bar */}
        <div className="glass p-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="auditiq-history-filters">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-steel">Industry</label>
            <Select value={industryFilter} onValueChange={setIndustryFilter}>
              <SelectTrigger className="mt-1 bg-navy-deep border-gold-dim/40 text-cream h-9" data-testid="filter-industry">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                <SelectItem value="all">All industries</SelectItem>
                {industries.map(i => <SelectItem key={i.key} value={i.key}>{i.display_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-steel">Status</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="mt-1 bg-navy-deep border-gold-dim/40 text-cream h-9" data-testid="filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="Complete">Complete</SelectItem>
                <SelectItem value="Running">Running</SelectItem>
                <SelectItem value="Failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-steel">Date from</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="mt-1 bg-navy-deep border-gold-dim/40 text-cream h-9" data-testid="filter-date-from" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-steel">Date to</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="mt-1 bg-navy-deep border-gold-dim/40 text-cream h-9" data-testid="filter-date-to" />
          </div>
        </div>

        {error && (
          <div className="card-premium glass border-l-4 border-[var(--auditiq-critical)] text-cream" data-testid="auditiq-history-error">
            {error}
          </div>
        )}

        {jobs === null && !error && (
          <div className="card-premium glass flex items-center gap-2 text-cream-dim">
            <Loader2 className="animate-spin" size={16} /> Loading…
          </div>
        )}

        {jobs && filtered.length === 0 && (
          <div className="card-premium glass text-center text-cream-dim" data-testid="auditiq-history-empty">
            {jobs.length === 0
              ? <>No audits yet. <Link to="/auditiq/upload" className="text-electric hover:text-gold underline">Upload a CSV to run your first audit.</Link></>
              : "No audits match the current filters."}
          </div>
        )}

        {jobs && filtered.length > 0 && (
          <div className="glass overflow-x-auto">
            <table className="w-full text-sm" data-testid="auditiq-history-table">
              <thead>
                <tr className="text-[11px] uppercase tracking-[0.18em] text-cream-dim border-b border-gold-dim/30">
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Industry</th>
                  <th className="text-left px-4 py-3">Template</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Findings</th>
                  <th className="text-right px-4 py-3">Exposure</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(j => (
                  <tr key={j.audit_id} className="border-b border-gold-dim/15 hover:bg-navy-soft/40">
                    <td className="px-4 py-3 text-cream">{fmtDate(j.created_at)}</td>
                    <td className="px-4 py-3 text-cream-dim capitalize">
                      {(j.industry || "municipality").replace(/_/g, " ")}
                    </td>
                    <td className="px-4 py-3 text-cream-dim">
                      {TEMPLATE_LABELS[j.template_key] || j.template_key}
                    </td>
                    <td className={`px-4 py-3 ${STATUS_CLASSES[j.status] || "text-cream-dim"}`}>{j.status}</td>
                    <td className="px-4 py-3 text-right text-cream">{j.findings_created || 0}</td>
                    <td className="px-4 py-3 text-right text-gold">{fmtMoney(j.estimated_savings || 0)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <Link to={`/auditiq/dashboard?audit_id=${j.audit_id}`}>
                          <Button variant="outline" size="sm" className="border-gold-dim/40 text-gold hover:bg-navy-soft"
                                  data-testid={`auditiq-view-${j.audit_id}`}>
                            View <ExternalLink size={12} className="ml-1" />
                          </Button>
                        </Link>
                        <Button variant="outline" size="sm"
                                onClick={() => exportPdf(j.audit_id)}
                                className="border-electric/40 text-electric hover:bg-navy-soft"
                                data-testid={`auditiq-export-${j.audit_id}`}>
                          <FileDown size={12} />
                        </Button>
                        {canDelete && (
                          <Button variant="outline" size="sm"
                                  onClick={() => setConfirmDelete(j)}
                                  className="border-[color:var(--auditiq-critical)]/40 text-[color:var(--auditiq-critical)] hover:bg-navy-soft"
                                  data-testid={`auditiq-delete-${j.audit_id}`}>
                            <Trash2 size={12} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
             data-testid="auditiq-delete-modal">
          <div className="glass max-w-md w-full p-6 relative">
            <button
              onClick={() => setConfirmDelete(null)}
              className="absolute top-3 right-3 text-cream-dim hover:text-gold"
              data-testid="auditiq-delete-modal-close"
            ><X size={18} /></button>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[color:var(--auditiq-critical)] mb-2">Confirm delete</div>
            <h3 className="font-display text-xl text-cream">Delete this audit?</h3>
            <div className="mt-4 text-sm text-cream-dim space-y-1 font-mono">
              <div><span className="text-steel">Organization:</span> {confirmDelete.organization_name || confirmDelete.organization_id || "—"}</div>
              <div><span className="text-steel">Template:</span> {confirmDelete.template_key}</div>
              <div><span className="text-steel">Date:</span> {fmtDate(confirmDelete.created_at)}</div>
              <div><span className="text-steel">Findings:</span> {confirmDelete.findings_created || 0}</div>
            </div>
            <div className="mt-5 text-xs text-cream-dim leading-relaxed">
              This permanently deletes all findings, scores, Chet outputs, transparency
              scores, savings data, and traffic analysis for this audit. This cannot be undone.
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button
                onClick={() => setConfirmDelete(null)}
                className="btn-ghost-gold h-10 px-4"
                data-testid="auditiq-delete-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={doDelete}
                disabled={deleting}
                className="bg-[color:var(--auditiq-critical)] text-white hover:bg-[color:var(--auditiq-critical)]/80 h-10 px-4"
                data-testid="auditiq-delete-confirm"
              >
                {deleting ? <><Loader2 size={14} className="animate-spin mr-2" /> Deleting…</> : "Delete Permanently"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PortalLayout>
  );
};

export default AuditHistory;
